import {
  COMPACT_GRID_WIDTH,
  EXPANDED_GRID_WIDTH,
  HEADER_HEIGHT,
} from '@act/react-components/app/constants';
import { Box, Drawer, IconButton } from '@mui/material';
import { CaretLineRightIcon } from '@phosphor-icons/react';
import { useContext } from 'react';
import { TrackingProvider, useTracking } from 'react-components/tracking';
import { callsAppSelectors } from 'redux/slices/callsSlice';
import { selectMemberById } from 'redux/slices/memberSlice';
import { useAppSelector } from 'redux/store-hooks';
import { ContentPanel } from './ContentPanel';
import DrawerTitle from './DrawerTitle';
import PostCallSummaryContext from './PostCallSummaryContext';

interface PostCallSummaryDrawerProps {
  open: boolean;
  displayMode: 'compact' | 'expanded';
  onClose: (transcription: string | null) => void;
}

// Separate component for the close button that gets tracking context
const DrawerCloseButton = ({
  onClose,
}: {
  onClose: (transcription: string | null) => void;
}) => {
  const tracking = useTracking();
  const { generateTrackingProps } = tracking || {};

  return (
    <IconButton
      {...(generateTrackingProps
        ? generateTrackingProps('close-drawer-button')
        : {})}
      onClick={() => onClose(null)}
      sx={{ ml: 'auto' }}
    >
      <CaretLineRightIcon size={32} weight="bold" />
    </IconButton>
  );
};

const PostCallSummaryDrawer = ({
  open,
  displayMode,
  onClose,
}: PostCallSummaryDrawerProps) => {
  const { voiceCallId } = useContext(PostCallSummaryContext);

  const voiceCall = callsAppSelectors.useSelectCallById(voiceCallId);
  const member = useAppSelector(selectMemberById(voiceCall?.subjectId));

  const marginLeft =
    displayMode === 'compact' ? COMPACT_GRID_WIDTH : EXPANDED_GRID_WIDTH;
  const width = `calc(100vw - ${marginLeft})`;

  return (
    <TrackingProvider contextName="post-call-summary-drawer">
      <Drawer
        open={open}
        onClose={() => onClose(null)}
        anchor="right"
        PaperProps={{
          sx: {
            marginTop: HEADER_HEIGHT,
            marginLeft: marginLeft,
            width: width,
            padding: '24px',
            boxSizing: 'border-box',
            transition: 'margin-left 0.5s ease-in-out, width 0.5s ease-in-out',
          },
        }}
      >
        <Box
          sx={{
            height: 'calc(100vh - 98px)',
            maxHeight: 'calc(100vh - 98px)',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
          }}
        >
          <Box
            sx={{ width: '100%', display: 'flex', alignItems: 'center', mb: 5 }}
          >
            <DrawerTitle member={member} voiceCall={voiceCall} />
            <DrawerCloseButton onClose={onClose} />
          </Box>

          <Box sx={{ flex: 1, minHeight: 0 }}>
            <ContentPanel voiceCall={voiceCall} />
          </Box>
        </Box>
      </Drawer>
    </TrackingProvider>
  );
};

export default PostCallSummaryDrawer;
